import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import * as PATHS from 'constants/paths';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import useCreateBusinessPlanHandler from 'pages/Workspace/hooks/useCreateBusinessPlanHandler';

import { ReactComponent as PlusIcon } from 'assets/icons/PlusIcon.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useBusinessPlanTypeAvailability } from 'hooks/useBusinessPlanTypeAvailability';

import { Button } from 'components/_basic/Button/Button';

import { checkIsHighestSubscription } from 'helpers/subscriptions';

import { ICreateProjectButtonProps } from './types';

import styles from './CreateProjectButton.module.scss';

export const CreateProjectButton: React.FC<ICreateProjectButtonProps> = ({
  template,
  label,
  icon,
  color,
  borderColor,
  backgroundColor,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isAvailable, limit } = useBusinessPlanTypeAvailability(template);

  const { createBusinessPlanHandler, isPending } = useCreateBusinessPlanHandler({
    isPlanAvailable: isAvailable,
    type: template,
  });

  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();

  const userSubscription = useUserSubscriptionStore(getUserSubscription);

  const isHighestSubscription = checkIsHighestSubscription(userSubscription?.currentSubscription.subscriptionPlan);

  const showButton = isAvailable || isHighestSubscription;

  const showUpgradeText = !isHighestSubscription && !isAvailable;

  const cssVariables = {
    '--plan-color': color,
    '--plan-background': backgroundColor,
    '--plan-borderColor': borderColor,
  } as React.CSSProperties;

  const handleUpgradeBusinessPlanClick = () => {
    navigate(PATHS.SUBSCRIPTION_PLAN);
  };

  return (
    <Box className={clsx(styles.buttonContainer, { [styles.disabled]: !isAvailable })} style={cssVariables}>
      <Box className={styles.templateInfo}>
        <Box className={styles.icon}>{icon}</Box>
        <Typography variant="h5" className={styles.title}>
          {label}
        </Typography>
        {(isAuthenticated || isAuthConfiguring) && (
          <Typography variant="subtitle1" className={styles.counter}>
            {limit} left
          </Typography>
        )}
      </Box>
      {showButton && (
        <Button
          onClick={createBusinessPlanHandler}
          variant="contained"
          size="small"
          className={styles.createButton}
          isLoading={isPending}
          loaderClassName={styles.loader}
          disabled={!isAvailable}
        >
          <PlusIcon />
        </Button>
      )}
      {showUpgradeText && (
        <Button color="secondary" variant="text" onClick={handleUpgradeBusinessPlanClick}>
          {t('upgrade')}
        </Button>
      )}
    </Box>
  );
};
